import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ListChecks } from 'lucide-react';
import { ChevronDown, ChevronRight, ChevronUp, X } from './icons';

const OTHER_LABEL_DEFAULT = 'Otro (especificar)';

const DropdownSelector = ({
    label,
    value,
    onChange,
    options = [],
    placeholder = 'Seleccionar',
    required = false,
    className = '',
    Icon,
    // Cuando la opción que se necesita no está en la lista: agrega "Otro
    // (especificar)" al final y, al elegirla, cambia a un campo de texto
    // libre. El valor guardado es directamente lo que la persona escribe
    // (no el texto "Otro"), así que un activo con un tipo/marca/etc. que ya
    // no está en la lista (o que nunca estuvo) se reconoce solo y abre
    // directo en modo texto en vez de mostrar el valor vacío.
    allowOther = false,
    otherLabel = OTHER_LABEL_DEFAULT,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const otherInputRef = useRef(null);

    const isCustomValue = allowOther && !!value && !options.includes(value);
    const [manualMode, setManualMode] = useState(isCustomValue);
    const userToggledRef = useRef(false);

    // Si el valor llega despues del primer render (ej. cargando un activo
    // existente de forma asincrona) y resulta ser un valor libre, entrar a
    // modo texto solo. No pisa la eleccion del usuario si ya toco el toggle.
    useEffect(() => {
        if (!userToggledRef.current && isCustomValue && !manualMode) {
            setManualMode(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCustomValue]);

    const updatePos = () => {
        if (!isOpen || !buttonRef.current) return;
        const r = buttonRef.current.getBoundingClientRect();
        setPos({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width });
    };

    useEffect(() => {
        if (!isOpen) return;
        updatePos();
        const onScroll = () => updatePos();
        const onResize = () => updatePos();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onDoc = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [isOpen]);

    const select = (opt) => {
        onChange(opt);
        setIsOpen(false);
    };

    const selectOther = () => {
        userToggledRef.current = true;
        setManualMode(true);
        onChange('');
        setIsOpen(false);
        // Esperar a que el input exista en el DOM antes de enfocarlo
        setTimeout(() => otherInputRef.current?.focus(), 0);
    };

    const backToList = () => {
        userToggledRef.current = true;
        setManualMode(false);
        onChange('');
    };

    const EffectiveIcon = Icon || (() => null);

    if (allowOther && manualMode) {
        return (
            <div className={`relative ${className}`}>
                {label && (
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {label} {required && <span className="text-red-500">*</span>}
                    </label>
                )}
                <div className="relative">
                    {Icon && (
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <EffectiveIcon className="h-4 w-4 text-gray-400" />
                        </div>
                    )}
                    <input
                        ref={otherInputRef}
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="Escribe la opción..."
                        className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-10 py-2.5 border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400`}
                    />
                    <button
                        type="button"
                        onClick={backToList}
                        title="Elegir de la lista"
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`relative ${className}`}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}
            <div className="relative" style={{ zIndex: isOpen ? 9999 : 'auto' }}>
                {Icon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <EffectiveIcon className="h-4 w-4 text-gray-400" />
                    </div>
                )}
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-10 py-2.5 text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400`}
                >
                    <span className={value ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                        {value || placeholder}
                    </span>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        {isOpen ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                    </div>
                </button>
            </div>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed z-[9999] bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    style={{ top: pos.top, left: pos.left, width: pos.width, minWidth: '260px' }}
                >
                    <div className="p-2">
                        {options.map((opt) => (
                            <button
                                key={opt}
                                type="button"
                                onClick={() => select(opt)}
                                className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-b-0 ${value === opt ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'} text-sm`}
                            >
                                <span className="text-sm">{opt}</span>
                            </button>
                        ))}
                        {allowOther && (
                            <button
                                type="button"
                                onClick={selectOther}
                                className="w-full px-3 py-2 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none border-t border-gray-200 dark:border-gray-600 text-blue-600 dark:text-blue-400 text-sm flex items-center gap-1.5 font-medium"
                            >
                                <ListChecks className="w-3.5 h-3.5" />
                                {otherLabel}
                            </button>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default DropdownSelector;
